import { Card, CardHeader, CardBody, Text, Flex, Box } from '@backstage/ui';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { appTranslationRef } from '../../../../core/i18n';
import { EmptyState } from '../../../../shared/components';
import {
  GITLAB_ANNOTATIONS,
  parseLanguagesAnnotation,
} from './gitlab.types';
import styles from './gitlab.module.css';

/**
 * Language color map for common languages
 */
const LANGUAGE_COLORS: Record<string, string> = {
  TypeScript: '#3178c6',
  JavaScript: '#f1e05a',
  Python: '#3572A5',
  Java: '#b07219',
  Go: '#00ADD8',
  Rust: '#dea584',
  Ruby: '#701516',
  PHP: '#4F5D95',
  'C#': '#178600',
  'C++': '#f34b7d',
  C: '#555555',
  Swift: '#ffac45',
  Kotlin: '#A97BFF',
  Scala: '#c22d40',
  HTML: '#e34c26',
  CSS: '#563d7c',
  SCSS: '#c6538c',
  Vue: '#41b883',
  Svelte: '#ff3e00',
  Shell: '#89e051',
  Dockerfile: '#384d54',
  YAML: '#cb171e',
  JSON: '#292929',
  Markdown: '#083fa1',
};

/**
 * Get color for a language, fallback to a generated color
 */
function getLanguageColor(language: string): string {
  if (LANGUAGE_COLORS[language]) {
    return LANGUAGE_COLORS[language];
  }
  // Generate a consistent color based on language name
  let hash = 0;
  for (let i = 0; i < language.length; i++) {
    hash = language.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = hash % 360;
  return `hsl(${hue}, 60%, 50%)`;
}

/**
 * Card displaying programming languages used in a GitLab repository
 * Reads data from the kpc.io/gitlab-languages annotation
 */
export const GitLabLanguagesCard = () => {
  const { entity } = useEntity();
  const { t } = useTranslationRef(appTranslationRef);

  const languagesAnnotation =
    entity.metadata.annotations?.[GITLAB_ANNOTATIONS.LANGUAGES];
  const languages = parseLanguagesAnnotation(languagesAnnotation);

  // Sort languages by percentage descending
  const sortedLanguages = languages
    ? Object.entries(languages).sort(([, a], [, b]) => b - a)
    : [];

  return (
    <Card>
      <CardHeader>
        <Text variant="title-small" weight="bold">
          {t('gitlab.languages.title')}
        </Text>
      </CardHeader>
      <CardBody>
        {sortedLanguages.length === 0 ? (
          <EmptyState
            title={t('gitlab.languages.emptyState.title')}
            description={t('gitlab.languages.emptyState.description')}
          />
        ) : (
          <Flex direction="column" gap="3">
            {/* Combined progress bar */}
            <div className={styles.progressBar}>
              {sortedLanguages.map(([language, percentage]) => (
                <div
                  key={language}
                  style={{
                    width: `${percentage}%`,
                    backgroundColor: getLanguageColor(language),
                  }}
                  title={`${language}: ${percentage.toFixed(1)}%`}
                />
              ))}
            </div>

            {/* Language list */}
            <Flex gap="3" className={styles.languageList}>
              {sortedLanguages.map(([language, percentage]) => (
                <Flex key={language} gap="1" align="center">
                  <Box
                    className={styles.colorDot}
                    style={{ backgroundColor: getLanguageColor(language) }}
                  />
                  <Text variant="body-small" color="secondary">
                    {language}
                  </Text>
                  <Text variant="body-small" color="secondary">
                    {percentage.toFixed(1)}%
                  </Text>
                </Flex>
              ))}
            </Flex>
          </Flex>
        )}
      </CardBody>
    </Card>
  );
};
